import { useState, useRef, useEffect, useContext } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { Button, Form, Modal, OverlayTrigger, Tooltip, Col, Row } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import Select from "../../components/AppSelect";
import { DateTime, IANAZone } from 'luxon';

import Sidebar from '../../components/Navigation/Sidebar';
import Topbar from '../../components/Navigation/Topbar';
import Grid from '../../components/Grid';
import Timeline from '../../components/Timeline';
import Loader from '../../components/Loader';

import { Show } from '../../hooks/Show';
import { useAuth } from '../../hooks/useAuth';
import { PermissionContext } from '../../hooks/PermissionContext';

import { ConvertToMoney } from '../../utils/formattingUtils';
import formatDateWithCustomFormat, { FormatsDate } from '../../utils/formatDate';
import { getStatusBadgeByCode } from '../../utils/StatusUtils';
import { HideByRole } from '../../utils/functionsUtils';
import { hasNoCountryLimitations } from '../../utils/functionsUtils';
import { buildOperatorGroupedOptions } from '../../utils/operatorGrouping';
import GroupedSelect from '../../components/GroupedSelect';
import UploadAddressFile from '../../components/Modal/Address/uploadAddressFile';

const Swal = require('sweetalert2');

const Address = ({
  reduxGetClients,
  clients,
  reduxGetLogisticsOperators,
  logisticsOperators,
  reduxGetHistoricSenders,
  historicSenders,
  reduxGetHistoricOperators,
  historicOperators,
  reduxGetStatuses,
  statuses,
  reduxGetAddresses,
  addresses,
  requestingFetchAddresses,
  countries,
  reduxGetCountries,
  reduxResetAddressForm,
  fileUploaded,
  fileUploadedTrackingId,
  reduxResetAddressesEvents,
  reduxGetBranches,
  branches,
  reduxUpdateAddressFile,
  reduxDownloadInvalidAddresses,
  successfulUploadInvalidAddresses,
  fetchInvalidAddressesData,
  novelties,
  reduxGetNovelties,
  reduxUploadUpdateFile,
}) => {
  const { hasPermission } = useContext(PermissionContext);

  const {
    register,
    watch,
    reset,
    setValue,
    getValues,
  } = useForm({});

  const {
    register: registerUpload,
    handleSubmit: handleSubmitUpload,
    formState: { errors: errorsUpload },
    reset: resetUpload,
    setValue: setValueUpload,
    watch: watchUpload,
  } = useForm({});

  const {
    register: registerUpdateAddress,
    handleSubmit: handleSubmitUpdateAddress,
    watch: watchUpdateAddress,
    formState: { errors: errorsUploadUpdateAddress },
    reset: resetUpdateAddress,
    setValue: setValueUpdateAddress,
    getValues: getValueUpdateAddress,
  } = useForm({});

  const {
    register: registerUpdateNewAddress,
    handleSubmit: handleSubmitUpdateNewAddress,
    watch: watchUpdateNewAddress,
    formState: { errors: errorsUploadUpdateNewAddress },
    reset: resetUpdateNewAddress,
    setValue: setValueUpdateNewAddress,
    getValues: getValueUpdateNewAddress,
  } = useForm({});

  const {
    register: registerChangeOperator,
    handleSubmit: handleSubmitChangeOperator,
    watch: watchUploadChangeOperator,
    formState: { errors: errorsChangeOperator },
    reset: resetChangeOperator,
    setValue: setValueChangeOperator,
    getValues: getValuesChangeOperator,
  } = useForm({});

  const {
    register: registerUploadTrackingId,
    handleSubmit: handleSubmitUploadTrackingId,
    watch: watchUploadTrackingId,
    formState: { errors: errorsUploadTrackingId },
    reset: resetUploadTrackingId,
    setValue: setValueUploadTrackingId,
    getValues: getValuesUploadTrackingId,
  } = useForm({});

  const dispatch = useDispatch();

  const {
    control: downloadFormControl,
    formState: { errors: downloadFormErrors },
    handleSubmit: handleSubmitDownload,
    watch: downloadFormWatchValue,
    reset: resetDownloadForm,
    clearErrors: clearDownloadFormErrors,
    setValue: setValueDownloadForm,
  } = useForm({});

  const userData = JSON.parse(window.localStorage.getItem('userData'));
  const countryUser = userData?.idCountry;
  const viewAllCountriesData = hasNoCountryLimitations();

  const history = useHistory();
  const location = useLocation();

  // DT-431: resolved país for the Histórico download form (buttonType === 3) cascade.
  // Multi-country users: derived from the editable País select. Fixed-country users:
  // derived directly from the session (País select is hidden for them).
  const resolvedIdCountry = viewAllCountriesData
    ? downloadFormWatchValue('country')?.value
    : countryUser;

  const [currentPage, setCurrentPage] = useState(1);
  const [offset, setOffset] = useState(10);
  const [fetchToken, setFetchToken] = useState(0);
  const [hydrated, setHydrated] = useState(false);

  const resetPagination = () => {
    setCurrentPage(1);
    setFetchToken((token) => token + 1);
  };
  const [showUpload, setShowUpload] = useState(false);
  const [showUpdateAddress, setShowUpdateAddress] = useState(false);
  const [showUpdateNewAddress, setShowUpdateNewAddress] = useState(false);

  const [fileName, setFileName] = useState(null);
  const [fileError, setFileError] = useState(null);

  const [fileNameUpdateAddress, setFileNameUpdateAddress] = useState(null);
  const [fileErrorUpdateAddress, setFileErrorUpdateAddress] = useState(null);

  const [fileNameUpdateNewAddress, setFileNameUpdateNewAddress] = useState(null);
  const [fileErrorUpdateNewAddress, setFileErrorUpdateNewAddress] = useState(null);

  const { session } = useAuth();

  const [showTimeline, setShowTimeline] = useState(false);
  const [idAddressSelected, setIdAddressSelected] = useState(null);
  const [showModalDates, setShowModalDates] = useState(false);
  const [startDateFrom, setStartDateFrom] = useState(null);
  const [startDateTo, setStartDateTo] = useState(null);
  const [buttonType, setButtonType] = useState(null);
  const [search, setSearch] = useState('');

  const indicativeRoad = useRef({});
  indicativeRoad.current = watch('indicativeRoad', '');

  const roadNumber = useRef({});
  roadNumber.current = watch('roadNumber', '');

  const appendixRoad = useRef({});
  appendixRoad.current = watch('appendixRoad', '');

  const crossoverRoad = useRef({});
  crossoverRoad.current = watch('crossoverRoad', '');

  const crossoverNumber = useRef({});
  crossoverNumber.current = watch('crossoverNumber', '');

  const appendixCrossingNumber = useRef({});
  appendixCrossingNumber.current = watch('appendixCrossingNumber', '');

  const orientationCrossingNumber = useRef({});
  orientationCrossingNumber.current = watch('orientationCrossingNumber', '');

  const insideTypes = useRef({});
  insideTypes.current = watch('insideTypes', '');

  const doorNumber = useRef({});
  doorNumber.current = watch('doorNumber', '');

  const inside = useRef({});
  inside.current = watch('inside', '');

  const file = useRef({});
  file.current = watchUpload('file', '');

  const fileUpdateAddress = useRef({});
  fileUpdateAddress.current = watchUpdateAddress('file', '');

  const fileUpdateNewAddress = useRef({});
  fileUpdateNewAddress.current = watchUpdateNewAddress('file', '');

  const state = useRef({});
  state.current = watch('state', '');

  const novelty = useRef({});
  novelty.current = watch('novelty', '');

  const client = useRef({});
  client.current = watch('senderIds', '');

  const operator2 = useRef({});
  operator2.current = watch('operatorIds', '');

  const branch = useRef({});
  branch.current = watch('branch', '');

  const customStyles = {
    menu: (base) => ({ ...base, zIndex: 9999 }),
    valueContainer: (baseStyles) => ({ ...baseStyles, maxHeight: "3.5vw", overflowY: "auto" }),
    control: (baseStyles) => ({ ...baseStyles, flexWrap: "nowrap" })
  };

  const CustomInput = ({ value, onClick, className }) => (
    <div className='input-group'>
      <input
        className={`form-control ${className}`}
        value={value}
        onClick={onClick}
        placeholder='dd/mm/yyyy'
      />
      <div className='input-group-prepend'>
        <button
          type='button'
          className='input-group-text'
          onClick={onClick}>
          <i className='fas fa-solid fa-calendar'></i>
        </button>
      </div>
    </div>
  );

  const handleOpenTimeline = (id) => {
    setIdAddressSelected(id);
    setShowTimeline(true);
  };

  const handleCloseTimeline = () => {
    reduxResetAddressesEvents();
    setIdAddressSelected(null);
    setShowTimeline(false);
  };

  const handleCloseUpdateAddress = () => {
    setFileNameUpdateAddress(null);
    resetUpdateAddress();
    reduxResetAddressForm();
    setFileErrorUpdateAddress(null);
    setShowUpdateAddress(false);
  };

  const handleCloseUpdateNewAddress = () => {
    setFileNameUpdateNewAddress(null);
    resetUpdateNewAddress();
    reduxResetAddressForm();
    setFileErrorUpdateAddress(null);
    setShowUpdateNewAddress(false);
  };

  const handleShowUpload = () => {
    setShowUpload(true);
  };

  const handleCloseUpload = (reloadTable) => {
    setShowUpload(false);

    if (reloadTable === true) {
      searchByFilter();
    }
  };

  const handleShowUploadUpdateAddress = () => {
    setFileNameUpdateAddress(null);
    resetUpdateAddress();
    setFileErrorUpdateAddress(null);
    setShowUpdateAddress(true);
  };

  const handleShowUploadUpdateNewAddress = () => {
    setFileNameUpdateNewAddress(null);
    resetUpdateNewAddress();
    setFileErrorUpdateNewAddress(null);
    setShowUpdateNewAddress(true);
  };

  const rescheduled = (items) => {
    let maxDate = null;
    items.forEach((item) => {
      if (item.date !== null && item.date !== '' && item.idNewState === 1) {
        const currentDate = new Date(item.date);
        if (maxDate === null || currentDate > maxDate) {
          maxDate = currentDate;
        }
      }
    });

    if (maxDate !== null) {
      const formattedDate = formatDate(maxDate);
      return formattedDate;
    } else {
      return '';
    }
  };

  const formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
  };

  const columns = [
    {
      title: '#',
      render: (rowData) => {
        return <span>{rowData.idAddress}</span>;
      },
      field: 'idAddress',
      searchable: true,
    },
    {
      title: 'ID externo',
      render: (rowData) => {
        return <span>{rowData.externalId}</span>;
      },
      field: 'externalId',
      searchable: true,
    },
    {
      title: 'Guía',
      render: (rowData) => {
        return <span>{rowData.trackingId}</span>;
      },
      field: 'trackingId',
      searchable: true,
    },
    {
      title: 'Estado',
      render: (rowData) => { return getStatusBadgeByCode(rowData.newState.code) },
    },
    {
      title: 'Novedad',
      render: (rowData) => { return rowData.novelty?.description },
      visible: !hasPermission("feature:isClient")
    },
    {
      title: 'Guía de Retorno',
      render: (rowData) => {
        return <span>{rowData.returnTrackingId}</span>;
      },
      field: 'returnTrackingId',
      searchable: true,
    },
    ...(hasPermission('feature:show-operator-address')
      ? [
        {
          title: 'Operador',
          render: (rowData) => {
            return <span>{rowData.operator ? rowData.operator.description : ''}</span>;
          },
          field: 'operator',
          searchable: false,
        },
      ]
      : []),
    {
      title: 'Destinatario',
      render: (rowData) => {
        return (
          <span>
            {rowData.firstName} {rowData.lastName}
          </span>
        );
      },
      field: 'firstName-lastName',
      searchable: true,
    },
    ...(hasPermission('feature:show-recipient-address')
      ? [
        {
          title: 'Dirección Destinatario',
          render: (rowData) => {
            return <span>{rowData.address}</span>;
          },
          field: 'address',
          searchable: true,
        },
      ]
      : []),
    ...(hasPermission('feature:show-recipient-address')
      ? [
        {
          title: 'Coordenadas',
          render: (rowData) => {
            return <span>{rowData.coordinates}</span>;
          },
          field: 'coordinates',
          searchable: true,
        },
      ]
      : []),
    ...(hasPermission('feature:show-reference1-address')
      ? [
        {
          title: 'Teléfono Destinatario',
          render: (rowData) => {
            return (
              <span>{rowData.reference1}</span>
            );
          },
          field: 'reference1',
          searchable: true,
        },
      ]
      : []),
    {
      title: 'Observaciones',
      render: (rowData) => {
        return <span>{rowData.reference2}</span>;
      },
      field: 'reference2',
      searchable: true,
      visible: !HideByRole(session?.role.name)
    },
    {
      title: 'Valor a recaudar',
      render: (rowData) => {
        return <span>{ConvertToMoney(rowData.collection)}</span>;
      },
      field: 'collection',
      searchable: true,
    },
    {
      title: 'Ciudad',
      render: (rowData) => {
        return <span>{rowData.city ? rowData.city.description : ''}</span>;
      },
      field: 'city.description',
      searchable: true,
    },
    {
      title: 'Departamento',
      render: (rowData) => {
        return <span>{rowData.city && rowData.city.department ? rowData.city.department.description : ''}</span>;
      },
      field: 'city.department.description',
      searchable: true,
    },
    ...(hasPermission('feature:show-client-address')
      ? [
        {
          title: 'Remitente',
          render: (rowData) => {
            return <span>{rowData.company ? rowData.company.description : ''}</span>;
          },
          field: 'company.description',
          searchable: true,
        },
      ]
      : []),
    ...(hasPermission('feature:show-branch')
      ? [
        {
          title: 'Bodega',
          render: (rowData) => {
            return <span>{rowData.branch ? rowData.branch.branchName : ''}</span>;
          },
          field: 'branch.branchName',
          searchable: true,
        },
      ]
      : []),
    {
      title: 'Fecha de reprogramacion',
      render: (rowData) => {
        return <span>{rescheduled(rowData.addressRecords)}</span>;
      },
      field: 'rescheduledDate',
      searchable: true,
    },
    {
      title: 'Opciones',
      render: (rowData) => {
        return (
          <>
            {rowData.deleted === 0 && (
              <Show when='feature:see-timeline'>
                <button
                  title='Timeline'
                  className='btn btn-primary btn-sm  btn-circle mr-2'
                  type='button'
                  onClick={(e) => handleOpenTimeline(rowData.idAddress)}>
                  <i className='fas fa-info fa-xs'></i>
                </button>
              </Show>
            )}
            {
              rowData.trackingId && (
                <>
                  <Show when='feature:generate-guide-pdf'>
                    <OverlayTrigger
                      placement='top'
                      delay={{ show: 50, hide: 50 }}
                      overlay={renderTooltip('Descargar guía')}>
                      <button
                        title='Descargar guía'
                        className='btn btn-secondary btn-sm  btn-circle mr-2'
                        type='button'
                        onClick={(e) => generateGuidePDF(rowData.trackingId)}>
                        <i className='fas fa fa-download fa-xs'></i>
                      </button>
                    </OverlayTrigger>
                  </Show >
                </>
              )
            }
          </>
        );
      },
    },
  ];

  const generateGuidePDF = async (trackingId) => {
    dispatch({
      type: 'SHOW_LOADING',
    });

    fetch(`${process.env.REACT_APP_API_URL}/api/v1/pdf-tracking/generateGuidePDF/${trackingId}`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${window.localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      }),
    })
      .then((response) => {
        if (response.status >= 200 && response.status < 300) {
          return response.blob();
        }

        if (response.status === 401) {
          window.localStorage.removeItem('token');
          return;
        }

        const error = new Error(response.statusText);
        error.response = response;
        throw error;
      })
      .then((blob) => {
        dispatch({
          type: 'HIDE_LOADING',
        });
        let url = window.URL.createObjectURL(blob);
        let a = document.createElement('a');
        a.href = url;
        a.download = `${DateTime.now().setZone(new IANAZone('America/Bogota')).toFormat('yyyy-MM-dd')}-${trackingId}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      })
      .catch((err) => {
        dispatch({
          type: 'HIDE_LOADING',
        });
        console.log(err);
      });
  };

  const handleChangeMultiSelect = (type, value) => {
    if (type === 'Sender') {
      const newSenderIds = {
        ...getValues('senderIds'),
        value
      }
      setValue("senderIds", String(newSenderIds.value.map(item => item.value)));
    } else if (type === 'Operator') {
      const newOperatorIds = {
        ...getValues('operatorIds'),
        value
      }
      setValue("operatorIds", String(newOperatorIds.value.map(item => item.value)));
    }
  }

  const buildFilters = ({ page = 1, offset: offsetOverride, search: searchOverride } = {}) => {
    let filters = {
      offset: offsetOverride ?? offset,
      search: searchOverride ?? search,
      page,
    };
    if (client.current) {
      filters = { ...filters, senderIds: client.current };
    }
    if (operator2.current) {
      filters = { ...filters, operatorIds: operator2.current };
    }
    if (state.current) {
      filters = { ...filters, state: state.current };
    }
    if (novelty.current) {
      filters = { ...filters, novelty: novelty.current };
    }
    if (branch.current) {
      filters = { ...filters, idBranch: branch.current };
    }
    if (startDateFrom && startDateTo) {
      filters = {
        ...filters,
        from: formatDateWithCustomFormat(startDateFrom, {
          format: FormatsDate.FULL24,
        }),
        to: formatDateWithCustomFormat(startDateTo, {
          format: FormatsDate.FULL24,
        }),
      };
    }
    return filters;
  };

  const syncFiltersToUrl = () => {
    const params = new URLSearchParams();
    params.set('limit', String(offset));
    params.set('page', String(currentPage));
    if (search) params.set('search', search);
    if (state.current) params.set('state', state.current);
    if (novelty.current) params.set('novelty', novelty.current);
    if (client.current) params.set('senderIds', client.current);
    if (operator2.current) params.set('operatorIds', operator2.current);
    if (branch.current) params.set('idBranch', branch.current);
    if (startDateFrom) params.set('from', startDateFrom.toISOString());
    if (startDateTo) params.set('to', startDateTo.toISOString());
    history.replace({ pathname: location.pathname, search: params.toString() });
  };

  const searchByFilter = () => {
    resetPagination();
  };

  const onSubmitUploadUpdateInvalidAddress = (data) => {
    const file = data.file[0];
    if (file && file.type === 'text/csv') {
      const reader = new FileReader();
      reader.readAsText(file);

      reader.onload = () => {
        const csvData = reader.result;

        const isCorrectDelimited = csvData.split('\n')[0].split(';').length > 1;

        if (!isCorrectDelimited) {
          return Swal.fire('Archivo errado', 'Señor usuario el archivo debe tener el formato \'CSV UTF-8 delimitado por comas\'', 'error');
        } else {
          reduxUpdateAddressFile({
            file: file
          });

          resetUpdateAddress();
          setFileNameUpdateAddress(null);
          reduxResetAddressForm();
          setFileErrorUpdateAddress(null);
          setShowUpdateAddress(false);
        }
      };
    } else return Swal.fire('Archivo errado', 'El archivo no cumple con el formato establecido <br/> Señor usuario el archivo debe tener el formato \'CSV UTF-8 delimitado por comas\'', 'error');
  };

  const onSubmitUploadUpdateAddress = (data) => {
    const file = data.file[0];
    if (file && file.type === 'text/csv') {
      const reader = new FileReader();
      reader.readAsText(file);

      reader.onload = () => {

        const validateCSV = (csvData) => {
          if (!csvData || csvData.trim().length === 0) {
            Swal.fire(
              'Archivo errado',
              'Señor Usuario, el archivo parece no tener datos. Por favor revíselo.',
              'error'
            );
            return false;
          }

          const rows = csvData.split('\n').filter(row => row.trim() !== '');
          const requiredColumns = 10;

          for (const row of rows) {
            const columns = row.split(';');
            if (columns.length !== requiredColumns) {
              Swal.fire(
                'Archivo errado',
                `Señor Usuario, el archivo debe tener exactamente ${requiredColumns} columnas. Por favor revise el archivo.`,
                'error'
              );
              return false;
            }
          }

          return true;
        };
        if (!validateCSV(reader.result)) {
          resetUpload();
          return;
        }
        const csvData = reader.result;

        const isCorrectDelimited = csvData.split('\n')[0].split(';').length > 1;

        if (!isCorrectDelimited) {
          return Swal.fire('Archivo errado', 'Señor usuario el archivo debe tener el formato \'CSV UTF-8 delimitado por comas\'', 'error');
        } else {
          reduxUploadUpdateFile({
            file: file,
            company:
              session && session.role.idCompany
                ? session.role.idCompany
                : data.idCompanyModal,
            city: data.idCityModal ? data.idCityModal : "",
          });

          resetUpdateNewAddress();
          setFileNameUpdateNewAddress(null);
          reduxResetAddressForm();
          setFileErrorUpdateNewAddress(null);
          setShowUpdateNewAddress(false);
        }
      };
    } else return Swal.fire('Archivo errado', 'El archivo no cumple con el formato establecido <br/> Señor usuario el archivo debe tener el formato \'CSV UTF-8 delimitado por comas\'', 'error');
  };

  const renderTooltip = (title) => <Tooltip id='button-tooltip'>{title}</Tooltip>;

  const handledChangeFrom = (date) => {
    setStartDateFrom(date);
    if (!date) {
      return setStartDateTo(date);
    }
    const toDate = new Date(date);
    toDate.setHours(23, 59, 59, 0);
    setStartDateTo(toDate);
  };

  const handledChangeTo = (date) => {
    setStartDateTo(date);
  };

  const openModalDates = (type) => {
    setShowModalDates(true);
    resetDownloadForm({
      endDate: null,
      startDate: null,
      senders: null,
      country: null,
      operator: null,
    });
    setButtonType(type);
    reduxGetCountries({
      page: 1,
      offset: 1000,
      search: '',
      isActive: true,
    });
  };

  const handleDownload = (data) => {
    Swal.fire(
      'Información',
      'El proceso puede tardar un poco... al finalizar se abrirá el Excel con la información.',
      'info'
    ).then((res) => {
      switch (buttonType) {
        case 1:
          handleShowDownload(data);
          dispatch({
            type: 'SHOW_LOADING',
          });
          break;
        case 2:
          handleShowDownloadAddressWithoutGuide(data);
          dispatch({
            type: 'SHOW_LOADING',
          });
          break;
        case 3:
          handleShowDownloadHistory(data);
          dispatch({
            type: 'SHOW_LOADING',
          });
          break;
        case 4:
          handleShowDownloadInvalidAddresses(data);
          dispatch({
            type: 'SHOW_LOADING',
          });
          break;
        default:
          break;
      }
    });

  };

  const handleShowDownload = (data) => {
    const token = window.localStorage.getItem('token');
    dispatch({
      type: 'HIDE_LOADING',
    });
    let _data = {
      idCompany: session.role.idCompany,
      role: session.role.idRole,
      start: formatDateWithCustomFormat(data.startDate, {
        format: 'YYYY-MM-DD 00:00:00',
      }),
      end: formatDateWithCustomFormat(data.endDate, {
        format: 'YYYY-MM-DD 23:59:59',
      }),
    };

    resetDownloadForm({
      endDate: null,
      startDate: null,
      senders: null,
      country: null,
      operator: null,
    });
    clearDownloadFormErrors();

    fetch(`${process.env.REACT_APP_REPORT_URL}/api/v1/addresses/download`, {
      method: 'POST',
      body: JSON.stringify(_data),
      headers: new Headers({
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      }),
    })
      .then((response) => {
        if (response.status === 401) {
          window.localStorage.removeItem('token');
          Swal.fire({
            icon: 'info',
            title: 'Token Expired',
            text: 'Your session has expired. Please log in again.',
          }).then(() => {
            window.location.href = '/';
          });
          return;
        } else {
          return response.blob();
        }
      })
      .then((blob) => {
        let url = window.URL.createObjectURL(blob);
        let a = document.createElement('a');
        a.href = url;
        a.download = DateTime.now().setZone(new IANAZone('America/Bogota')).toFormat('yyyy-MM-dd') + '-errors-addresses.csv';
        document.body.appendChild(a);
        a.click();
        a.remove();
        dispatch({
          type: 'HIDE_LOADING',
        });
      })
      .catch((err) => {
        dispatch({
          type: 'HIDE_LOADING',
        });
        console.log(err);
      });
  };

  const handleShowDownloadAddressWithoutGuide = (data) => {
    const token = window.localStorage.getItem('token');
    dispatch({
      type: 'HIDE_LOADING',
    });
    let _data = {
      idCompany: session.role.idCompany,
      start: formatDateWithCustomFormat(data.startDate, {
        format: 'YYYY-MM-DD 00:00:00',
      }),
      end: formatDateWithCustomFormat(data.endDate, {
        format: 'YYYY-MM-DD 23:59:59',
      }),
    };

    resetDownloadForm({
      endDate: null,
      startDate: null,
      senders: null,
      country: null,
      operator: null,
    });
    clearDownloadFormErrors();

    fetch(`${process.env.REACT_APP_REPORT_URL}/api/v1/addresses/downloadAddressWithoutGuide`, {
      method: 'POST',
      body: JSON.stringify(_data),
      headers: new Headers({
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      }),
    })
      .then((response) => {
        if (response.status === 401) {
          window.localStorage.removeItem('token');
          Swal.fire({
            icon: 'info',
            title: 'Token Expired',
            text: 'Your session has expired. Please log in again.',
          }).then(() => {
            window.location.href = '/';
          });
          return;
        } else {
          return response.blob();
        }
      })
      .then((blob) => {
        let url = window.URL.createObjectURL(blob);
        let a = document.createElement('a');
        a.href = url;
        a.download = DateTime.now().setZone(new IANAZone('America/Bogota')).toFormat('yyyy-MM-dd') + '-incomplete-addresses.csv';
        document.body.appendChild(a);
        a.click();
        a.remove();
        dispatch({
          type: 'HIDE_LOADING',
        });
      })
      .catch((err) => {
        dispatch({
          type: 'HIDE_LOADING',
        });
        console.log(err);
      });
  };

  const handleShowDownloadHistory = (data) => {
    const token = window.localStorage.getItem('token');
    dispatch({
      type: 'HIDE_LOADING',
    });
    let selectedCountryLabel = data.country ? data.country.label : null;
    if (!viewAllCountriesData) {
      const userCountry =
        countries && countries.items && countries.items.find((c) => c.idCountry == countryUser);
      if (userCountry) {
        selectedCountryLabel = userCountry.description;
      }
    }
    let _data = {
      role: session.role.idRole,
      idUser: session.idUser,
      start: formatDateWithCustomFormat(data.startDate, {
        format: FormatsDate.FULL24,
      }),
      end: formatDateWithCustomFormat(data.endDate, { format: FormatsDate.FULL24 }),
      senders: data.senders && String(data.senders.map(item => item.value)),
      country: selectedCountryLabel,
      operator: data.operator && String(data.operator.map(item => item.value)),
    };

    resetDownloadForm({
      endDate: null,
      startDate: null,
      senders: null,
      country: null,
      operator: null,
    });

    clearDownloadFormErrors();

    fetch(`${process.env.REACT_APP_REPORT_URL}/api/v1/addresses/downloadAllAddresses`, {
      method: 'POST',
      body: JSON.stringify(_data),
      headers: new Headers({
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      }),
    })
      .then((response) => {
        if (response.status === 401) {
          window.localStorage.removeItem('token');
          Swal.fire({
            icon: 'info',
            title: 'Token Expired',
            text: 'Your session has expired. Please log in again.',
          }).then(() => {
            window.location.href = '/';
          });
          return;
        } else {
          return response.blob();
        }
      })
      .then((blob) => {
        let url = window.URL.createObjectURL(blob);
        let a = document.createElement('a');
        a.href = url;
        a.download = DateTime.now().setZone(new IANAZone('America/Bogota')).toFormat('yyyy-MM-dd') + 'addresses.csv';
        document.body.appendChild(a);
        a.click();
        a.remove();
        dispatch({
          type: 'HIDE_LOADING',
        });
      })
      .catch((err) => {
        dispatch({
          type: 'HIDE_LOADING',
        });
        console.log(err);
      });
  };

  const handleShowDownloadInvalidAddresses = (data) => {
    let _data = {
      start: formatDateWithCustomFormat(data.startDate, {
        format: FormatsDate.FULL24,
      }),
      end: formatDateWithCustomFormat(data.endDate, { format: FormatsDate.FULL24 })
    };

    resetDownloadForm({
      endDate: null,
      startDate: null,
      senders: null,
      country: null,
      operator: null,
    });

    clearDownloadFormErrors();
    reduxDownloadInvalidAddresses(_data)
  };

  useEffect(() => {
    if (file.current != null && file.current.length > 0) {
      setFileName(file.current[0].name);
    }
  }, [file.current]);

  useEffect(() => {
    if (fileUpdateAddress.current != null && fileUpdateAddress.current.length > 0) {
      setFileNameUpdateAddress(fileUpdateAddress.current[0].name);
    }
  }, [fileUpdateAddress.current]);

  useEffect(() => {
    if (fileUpdateNewAddress.current != null && fileUpdateNewAddress.current.length > 0) {
      setFileNameUpdateNewAddress(fileUpdateNewAddress.current[0].name);
    }
  }, [fileUpdateNewAddress.current]);

  useEffect(() => {
    reduxGetClients();
    reduxGetLogisticsOperators();
    reduxGetStatuses({
      page: 1,
      search: '',
      offset: 1000,
    });
    reduxGetNovelties({
      page: 1,
      search: "",
      offset: 1000,
      status: "",
    });
    reduxResetAddressForm();
    reset();
    resetDownloadForm({
      endDate: null,
      startDate: null,
      senders: null,
      country: null,
      operator: null,
    });
    fileUploaded = null;
    reduxGetBranches({
      page: 1,
      offset: 1000,
      search: '',
      isActive: true,
    });
  }, []);

  // DT-431: re-fetch Remitente options for the Histórico download form when the
  // resolved país changes. Mirrors AssignOperator's hubWatcher cascade pattern.
  // Uses a dedicated action/state slice (historicSenders) instead of the shared
  // `clients` slice consumed by the main search filters and UploadAddressFile.
  useEffect(() => {
    if (!showModalDates || buttonType !== 3) return;

    setValueDownloadForm('senders', null);
    if (!resolvedIdCountry) return;

    reduxGetHistoricSenders({ idCountry: resolvedIdCountry });
  }, [resolvedIdCountry, showModalDates, buttonType]);

  // DT-431: re-fetch Operador options (DSP + Tradicional) for the Histórico
  // download form when the resolved país changes.
  useEffect(() => {
    if (!showModalDates || buttonType !== 3) return;

    setValueDownloadForm('operator', null);
    if (!resolvedIdCountry) return;

    reduxGetHistoricOperators({ idCountry: resolvedIdCountry });
  }, [resolvedIdCountry, showModalDates, buttonType]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('limit')) setOffset(Number(params.get('limit')));
    if (params.get('search')) setSearch(params.get('search'));
    if (params.get('from')) setStartDateFrom(new Date(params.get('from')));
    if (params.get('to')) setStartDateTo(new Date(params.get('to')));
    if (params.get('state')) setValue('state', params.get('state'));
    if (params.get('novelty')) setValue('novelty', params.get('novelty'));
    if (params.get('senderIds')) setValue('senderIds', params.get('senderIds'));
    if (params.get('operatorIds')) setValue('operatorIds', params.get('operatorIds'));
    if (params.get('idBranch')) setValue('branch', params.get('idBranch'));
    if (params.get('page')) setCurrentPage(Number(params.get('page')));
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    reduxGetAddresses(buildFilters({ page: currentPage }));
    syncFiltersToUrl();
  }, [currentPage, fetchToken, hydrated]);

  useEffect(() => {
    if (fileUploaded) {
      reduxGetAddresses(buildFilters({ page: currentPage }));
      if (fileUploaded.updateErrors === true && fileUploaded.invalidItems.length > 0) {
        const token = window.localStorage.getItem("token");
        fetch(
          `${process.env.REACT_APP_API_URL}/api/v1/addresses/invalidUpdateOrder`,
          {
            method: "POST",
            body: JSON.stringify(fileUploaded.invalidItems),
            headers: new Headers({
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            }),
          }
        )
          .then((response) => response.blob())
          .then((blob) => {
            let url = window.URL.createObjectURL(blob);
            let a = document.createElement("a");
            a.href = url;
            a.download = `invalid_update_orders_${DateTime.now().toISODate()}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
          });
      } else if (!fileUploaded.updateErrors && fileUploaded.invalidItems.length > 0) {
        const token = window.localStorage.getItem("token");
        fetch(
          `${process.env.REACT_APP_API_URL}/api/v1/addresses/invalidOrder`,
          {
            method: "POST",
            body: JSON.stringify(fileUploaded.invalidItems),
            headers: new Headers({
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            }),
          }
        )
          .then((response) => response.blob())
          .then((blob) => {
            let url = window.URL.createObjectURL(blob);
            let a = document.createElement("a");
            a.href = url;
            a.download = `unconfirmed_invalids_${DateTime.now().toISODate()}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
          });
      }

      Swal.fire(
        'Proceso exitoso!',
        'Se procesaron ' + fileUploaded.processed + " de " + fileUploaded.total + ' registros del archivo cargado.',
        'success'
      );
    }
  }, [fileUploaded]);

  useEffect(() => {
    if (fileUploadedTrackingId) {
      reduxGetAddresses(buildFilters({ page: currentPage }));
      Swal.fire(
        'Proceso exitoso!',
        'En total se procesaron ' + fileUploadedTrackingId.total + ' registros del archivo cargado.',
        'success'
      );
    }
  }, [fileUploadedTrackingId]);

  useEffect(() => {
    if (successfulUploadInvalidAddresses) {
      Swal.fire(
        'Proceso exitoso!',
        'Se procesaron ' + fetchInvalidAddressesData.processed + " de " + fetchInvalidAddressesData.total + ' registros del archivo cargado.',
        'success'
      );
    }
  }, [successfulUploadInvalidAddresses]);

  const renderHelpTooltipdownload = (title) => <Tooltip id='button-tooltip'>{title}</Tooltip>;

  const handleButtonUpdateClick = () => {
    const csvData = `*Pais;*Departamento;*Ciudad;Codigo Postal;Barrio;*Direccion;Comentarios;Valor a recaudar;Productos;*Id Address;*Esta Columna es obligatoria; **Recuerde eliminar esta linea`
    const fileName = 'templateLoadAddress.csv';
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <div>
      <div id='wrapper'>
        <Sidebar />
        <div
          id='content-wrapper'
          className='d-flex flex-column'>
          <div id='content'>
            <Topbar />
            <div className='container-fluid'>
              <div className='d-sm-flex align-items-center mb-1'>
                <h1 className='h3 mb-0 text-gray-800 mr-3'>Direcciones</h1>
                <div className='ml-2'>
                  <Show when='feature:upload-address'>
                    <OverlayTrigger
                      placement='top'
                      delay={{ show: 50, hide: 50 }}
                      overlay={renderTooltip('Cargar direcciones')}>
                      <button
                        className='btn btn-primary btn-circle'
                        type='button'
                        onClick={handleShowUpload}>
                        <i className='fas fa-upload fa-sm'></i>
                      </button>
                    </OverlayTrigger>
                  </Show>
                </div>
                {!(hasPermission('feature:isOperator') || hasPermission('feature:isClient')) && (
                  <div className='ml-2'>
                    <Show when='feature:upload-address'>
                      <OverlayTrigger
                        placement='top'
                        delay={{ show: 50, hide: 50 }}
                        overlay={renderTooltip('Actualizar direcciones')}>
                        <button
                          className='btn btn-primary btn-circle'
                          type='button'
                          onClick={handleShowUploadUpdateNewAddress}>
                          <i className='fas fa-edit fa-sm'></i>
                        </button>
                      </OverlayTrigger>
                    </Show>
                  </div>
                )}
                <div className='ml-2'>
                  <Show when='feature:download-address'>
                    <OverlayTrigger
                      placement='top'
                      delay={{ show: 50, hide: 50 }}
                      overlay={renderTooltip('Descargar direcciones erróneas')}>
                      <button
                        className='btn btn-danger btn-circle'
                        type='button'
                        onClick={(e) => openModalDates(1)}>
                        <i className='fas fa-download fa-sm'></i>
                      </button>
                    </OverlayTrigger>
                  </Show>
                </div>
                <div className='ml-2'>
                  <Show when='feature:download-address-without-guide'>
                    <OverlayTrigger
                      placement='top'
                      delay={{ show: 50, hide: 50 }}
                      overlay={renderTooltip('Descargar direcciones sin guía')}>
                      <button
                        className='btn btn-danger btn-circle'
                        type='button'
                        onClick={(e) => openModalDates(2)}>
                        <i className='fas fa-file-download fa-sm'></i>
                      </button>
                    </OverlayTrigger>
                  </Show>
                </div>
                <div className='ml-2'>
                  <Show when='feature:download-all-address'>
                    <OverlayTrigger
                      placement='top'
                      delay={{ show: 50, hide: 50 }}
                      overlay={renderTooltip('Descargar histórico')}>
                      <button
                        className='btn btn-info btn-circle'
                        type='button'
                        onClick={(e) => openModalDates(3)}>
                        <i className='fas fa-file-download fa-sm'></i>
                      </button>
                    </OverlayTrigger>
                  </Show>
                </div>
                <div className='ml-2'>
                  <Show when='feature:update-upload-invalid-address'>
                    <OverlayTrigger
                      placement='top'
                      delay={{ show: 50, hide: 50 }}
                      overlay={renderTooltip('Cargar direcciones inválidas')}>
                      <button
                        className='btn btn-warning btn-circle'
                        type='button'
                        onClick={handleShowUploadUpdateAddress}>
                        <i className='fas fa-upload fa-sm'></i>
                      </button>
                    </OverlayTrigger>
                  </Show>
                </div>
                <div className='ml-2'>
                  <Show when='feature:download-invalid-address'>
                    <OverlayTrigger
                      placement='top'
                      delay={{ show: 50, hide: 50 }}
                      overlay={renderTooltip('Descargar direcciones inválidas')}>
                      <button
                        className='btn btn-warning btn-circle'
                        type='button'
                        onClick={(e) => openModalDates(4)}>
                        <i className='fas fa-file-download fa-sm'></i>
                      </button>
                    </OverlayTrigger>
                  </Show>
                </div>
              </div>
              <p className='mb-4'>Módulo de Administración de direcciones</p>
              <div className='card shadow mb-4'>
                <div className='card-header py-3'>
                  <h6 className='m-0 font-weight-bold text-primary'>Listado de Direcciones</h6>
                </div>
                <div className='card-body'>
                  <Row>
                    <Col md={2.5} className='mx-3'>
                      <label>Desde</label>
                      <br />
                      <DatePicker
                        selected={startDateFrom}
                        onChange={(date) => handledChangeFrom(date)}
                        dateFormat='MM-dd-yyyy hh:mm a'
                        maxDate={new Date()}
                        showTimeSelect
                        peekNextMonth
                        className='form-control'
                        isClearable
                        timeIntervals={10}
                      />
                    </Col>
                    <Col md={2.5} >
                      <label>Hasta</label>
                      <br />
                      <DatePicker
                        selected={startDateTo}
                        onChange={(date) => handledChangeTo(date)}
                        dateFormat='MM-dd-yyyy hh:mm a'
                        maxDate={new Date()}
                        minDate={startDateFrom}
                        showTimeSelect
                        peekNextMonth
                        className='form-control'
                        isClearable
                        timeIntervals={10}
                        filterTime={(time) => {
                          if (!startDateFrom) {
                            return true;
                          }
                          const selectedTime = startDateFrom.getTime();
                          const currentTime = time.getTime();
                          return currentTime >= selectedTime;
                        }}
                      />
                    </Col>
                    <Col>
                      <Show when='feature:show-client-address'>
                        <label
                          htmlFor='senderIds'
                          className='form-label'>
                          Remitente
                        </label>
                        <Select
                          {...register('senderIds')}
                          id='senderIds'
                          onChange={(value) => handleChangeMultiSelect('Sender', value)}
                          isClearable={true}
                          isDisabled={!startDateFrom || !startDateTo}
                          isMulti
                          styles={customStyles}
                          options={
                            clients && Object.keys(clients).length > 0
                              ? clients.items
                                .filter((f) => f.isActive && (viewAllCountriesData || f.country == countryUser))
                                .map((e, key) => ({
                                  value: e.idCompany,
                                  label: e.description,
                                }))
                              : []
                          }
                        />
                      </Show>
                    </Col>
                    <Col>
                      <Show when='feature:show-operator-address'>
                        <label
                          htmlFor='operatorIds'
                          className='form-label'>
                          Operador
                        </label>
                        <Select
                          {...register('operatorIds')}
                          id='operatorIds'
                          onChange={(value) => handleChangeMultiSelect('Operator', value)}
                          isClearable={true}
                          isMulti
                          isDisabled={!startDateFrom || !startDateTo}
                          styles={customStyles}
                          options={
                            logisticsOperators &&
                            Object.keys(logisticsOperators).length > 0 &&
                            logisticsOperators.items
                              .filter((f) => f.isActive === true && (viewAllCountriesData || f.country == countryUser))
                              .map((ele, key) => ({
                                value: ele.idCompany,
                                label: ele.description,
                              }))
                          }
                        />
                      </Show>
                    </Col>
                    <Col>
                      <label
                        htmlFor='state'
                        className='form-label'>
                        Estado
                      </label>
                      <Select
                        isClearable={true}
                        isDisabled={!startDateFrom || !startDateTo}
                        styles={customStyles}
                        {...register('state')}
                        id='state'
                        onChange={(value) => setValue('state', value?.value || '')}
                        options={
                          statuses && Object.keys(statuses).length > 0
                            ? [
                              ...statuses.items.map((ele) => ({
                                value: ele.code,
                                label: ele.name,
                              })),
                              { value: '-2', label: 'Eliminados' },
                            ]
                            : []
                        }
                      />
                    </Col>
                    <Show whenNot={["feature:isClient"]} >
                      <Col>
                        <label
                          htmlFor='novelty'
                          className='form-label'>
                          Novedad
                        </label>
                        <Select
                          isClearable={true}
                          isDisabled={!startDateFrom || !startDateTo}
                          styles={customStyles}
                          {...register('novelty')}
                          id='novelty'
                          onChange={(value) => setValue('novelty', value?.value || '')}
                          options={
                            novelties &&
                              Array.isArray(novelties.items) &&
                              novelties.items.length > 0 &&
                              state.current
                              ? novelties.items
                                .filter(e => e.Status && e.Status.code == state.current)
                                .map(ele => ({
                                  value: ele.code,
                                  label: ele.description,
                                }))
                              : []
                          }
                        />
                      </Col>
                    </Show>
                    <Col>
                      <Show when='feature:show-branch'>
                        <label
                          htmlFor='branch'
                          className='form-label'>
                          Bodega
                        </label>
                        <Select
                          isClearable={true}
                          isDisabled={!startDateFrom || !startDateTo}
                          styles={customStyles}
                          {...register('branch')}
                          id='branch'
                          onChange={(value) => setValue('branch', value?.value || '')}
                          options={
                            branches && Object.keys(branches).length > 0
                              ? branches.items
                                .filter((b) => viewAllCountriesData || b.country.idCountry == countryUser)
                                .map((ele) => ({
                                  value: ele.idBranch,
                                  label: ele.branchName,
                                }))
                              : []
                          }
                        />
                      </Show>
                    </Col>
                    <Col>
                      <button className='btn btn-primary' onClick={searchByFilter} style={{ marginTop: "32px" }}>Buscar</button>
                    </Col>
                  </Row>
                  <br />
                  {requestingFetchAddresses && <Loader show={requestingFetchAddresses} />}
                  <Grid
                    className='stickyAddress'
                    cols={columns}
                    data={addresses && Object.keys(addresses).length > 0 ? addresses.items : []}
                    offset={offset}
                    onChangeRange={(value) => {
                      setOffset(value);
                      resetPagination();
                    }}
                    defaultValue={search}
                    onChangeSearch={(value) => {
                      setSearch(value);
                      resetPagination();
                    }}
                  />
                  <div className='row mt-3 align-items-center'>
                    <div className='col-sm-12 col-md-5'>
                      {!requestingFetchAddresses &&
                        addresses &&
                        addresses.items &&
                        addresses.items.length === 0 && (
                          <span>Sin resultados para los filtros aplicados</span>
                        )}
                    </div>
                    <div className='col-sm-12 col-md-7'>
                      {addresses && addresses.items && addresses.items.length > 0 && (
                      <div className='btn-group float-right'>
                        <button
                          type='button'
                          className='btn btn-outline-primary'
                          disabled={currentPage === 1 || requestingFetchAddresses}
                          onClick={() => setCurrentPage((page) => page - 1)}
                        >
                          Anterior
                        </button>
                        {(() => {
                          const WINDOW = 10;
                          const totalPages = Math.max(
                            currentPage + (addresses && addresses.hasMore ? 1 : 0),
                            1
                          );
                          let startPage = Math.max(1, currentPage - Math.floor(WINDOW / 2));
                          const endPage = Math.min(totalPages, startPage + WINDOW - 1);
                          startPage = Math.max(1, endPage - WINDOW + 1);
                          return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map((page) => (
                            <button
                              key={page}
                              type='button'
                              className={page === currentPage ? 'btn btn-primary' : 'btn btn-outline-primary'}
                              disabled={requestingFetchAddresses}
                              onClick={() => setCurrentPage(page)}
                            >
                              {page}
                            </button>
                          ));
                        })()}
                        <button
                          type='button'
                          className='btn btn-outline-primary'
                          disabled={!(addresses && addresses.hasMore) || requestingFetchAddresses}
                          onClick={() => setCurrentPage((page) => page + 1)}
                        >
                          Siguiente
                        </button>
                      </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div>
        <Modal
          size='xl'
          show={showTimeline}
          onHide={handleCloseTimeline}
          onEscapeKeyDown={handleCloseTimeline}
          backdrop='static'
          keyboard={true}>
          <Modal.Header closeButton>
            <Modal.Title>Timeline</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Timeline idAddress={idAddressSelected}></Timeline>
          </Modal.Body>
        </Modal>
      </div>
      <div>
        <UploadAddressFile
          show={showUpload}
          shippers={clients}
          handleClose={handleCloseUpload}
        />
      </div>
      <div>
        <Modal
          show={showUpdateNewAddress}
          onHide={handleCloseUpdateNewAddress}
          onEscapeKeyDown={handleCloseUpdateNewAddress}
          backdrop='static'
          keyboard={true}>
          <form onSubmit={handleSubmitUpdateNewAddress(onSubmitUploadUpdateAddress)}>
            <Modal.Header closeButton>
              <Modal.Title>Actualizar Direcciones</Modal.Title>
              <OverlayTrigger
                placement='top'
                delay={{ show: 50, hide: 50 }}
                overlay={renderHelpTooltipdownload("Descargar plantilla")}>
                <button className="btn btn-primary btn-circle ml-2 mr-2" type="button"
                  onClick={handleButtonUpdateClick}>
                  <i className="fas fa-download fa-sm"></i>
                </button>
              </OverlayTrigger>
            </Modal.Header>
            <Modal.Body>
              <div className="row">
                <div className="form-group col-md-12">
                  <label htmlFor="file" className="form-label">
                    Archivo
                  </label>
                  <div className="custom-file">
                    <input
                      type='file'
                      id='file'
                      name='file'
                      {...registerUpdateNewAddress('file', { required: true })}
                      className={`custom-file-input ${errorsUploadUpdateNewAddress.file && 'is-invalid'}`}
                    />{' '}
                    <label
                      className='custom-file-label'
                      htmlFor='validatedCustomFile'>
                      {fileError ? fileError : fileNameUpdateNewAddress ? fileNameUpdateNewAddress : 'Seleccionar archivo...'}
                    </label>
                  </div>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant='secondary'
                onClick={handleCloseUpdateNewAddress}>
                Cancelar
              </Button>
              <Button
                variant='primary'
                type='submit'>
                Guardar
              </Button>
            </Modal.Footer>
          </form>
        </Modal>
      </div>
      <div>
        <Modal
          show={showUpdateAddress}
          onHide={handleCloseUpdateAddress}
          onEscapeKeyDown={handleCloseUpdateAddress}
          backdrop='static'
          keyboard={true}>
          <form onSubmit={handleSubmitUpdateAddress(onSubmitUploadUpdateInvalidAddress)}>
            <Modal.Header closeButton>
              <Modal.Title>Actualizar direcciones erroneas</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="row">
                <div className="form-group col-md-12">
                  <label htmlFor="file" className="form-label">
                    Archivo
                  </label>
                  <div className="custom-file">
                    <input
                      type='file'
                      id='file'
                      name='file'
                      {...registerUpdateAddress('file', { required: true })}
                      className={`custom-file-input ${errorsUploadUpdateAddress.file && 'is-invalid'}`}
                    />{' '}
                    <label
                      className='custom-file-label'
                      htmlFor='validatedCustomFile'>
                      {fileErrorUpdateAddress ? fileErrorUpdateAddress : fileNameUpdateAddress ? fileNameUpdateAddress : 'Seleccionar archivo...'}
                    </label>
                  </div>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant='secondary'
                onClick={handleCloseUpdateAddress}>
                Cancelar
              </Button>
              <Button
                variant='primary'
                type='submit'>
                Guardar
              </Button>
            </Modal.Footer>
          </form>
        </Modal>
      </div>
      <div>
        <Modal
          size='lg'
          show={showModalDates}
          onHide={() => {
            setShowModalDates(false);
          }}
          onEscapeKeyDown={() => setShowModalDates(false)}
          backdrop='static'
          keyboard={true}>
          <Modal.Header closeButton>
            <Modal.Title>
              {buttonType === 1
                ? 'Descargar direcciones erróneas'
                : buttonType === 2
                  ? 'Descargar direcciones sin guía'
                  : buttonType === 3
                    ? 'Descargar histórico'
                    : 'Descargar direcciones invalidas'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <FormProvider>
              <Form onSubmit={handleSubmitDownload(handleDownload)}>
                <Row>
                  <Form.Group as={Col}>
                    <div>Desde</div>
                    <Controller
                      control={downloadFormControl}
                      name='startDate'
                      rules={{ required: true }}
                      render={({ field: { onChange, value } }) => (
                        <DatePicker
                          required
                          selected={value}
                          onChange={onChange}
                          showTimeSelect={buttonType === 3}
                          customInput={<CustomInput className={`${downloadFormErrors.startDate && 'is-invalid'}`} />}
                          peekNextMonth
                          dateFormat={buttonType === 3 ? 'dd/MM/yyyy hh:mm aa' : 'dd/MM/yyyy'}
                          maxDate={new Date()}
                          timeIntervals={10}
                        />
                      )}
                    />
                  </Form.Group>
                  <Form.Group as={Col}>
                    <div>Hasta</div>
                    <Controller
                      control={downloadFormControl}
                      name='endDate'
                      rules={{ required: true }}
                      render={({ field: { onChange, value } }) => (
                        <DatePicker
                          required
                          selected={value}
                          onChange={onChange}
                          showTimeSelect={buttonType === 3}
                          customInput={<CustomInput className={`${downloadFormErrors.endDate && 'is-invalid'}`} />}
                          peekNextMonth
                          dateFormat={buttonType === 3 ? 'dd/MM/yyyy hh:mm aa' : 'dd/MM/yyyy'}
                          maxDate={new Date()}
                          minDate={downloadFormWatchValue('startDate')}
                          timeIntervals={10}
                          filterTime={(time) => {
                            const dateStart = downloadFormWatchValue('startDate');
                            if (!dateStart) {
                              return true;
                            }
                            const selectedTime = dateStart.getTime();
                            const currentTime = time.getTime();
                            return currentTime >= selectedTime;
                          }}
                        />
                      )}
                    />
                  </Form.Group>
                </Row>
                {
                  buttonType === 3 &&
                  <Show whenNot={['feature:isClient', 'feature:isOperator']}>
                    {
                      viewAllCountriesData &&
                      <Row>
                        <Form.Group as={Col}>
                          <div>País</div>
                          <Controller
                            name='country'
                            control={downloadFormControl}
                            render={({ field }) => (
                              <Select
                                {...field}
                                isDisabled={!downloadFormWatchValue('startDate') || !downloadFormWatchValue('endDate')}
                                isClearable={true}
                                styles={customStyles}
                                options={
                                  countries && Object.keys(countries).length > 0
                                    ? countries.items
                                      .filter((f) => f.isActive)
                                      .map((ele, key) => ({
                                        value: ele.idCountry,
                                        label: ele.description,
                                      }))
                                    : []
                                }
                              />
                            )}
                          />
                        </Form.Group>
                      </Row>
                    }
                    <Row>
                      <Form.Group as={Col}>
                        <div>Remitente</div>
                        <Controller
                          name='senders'
                          control={downloadFormControl}
                          render={({ field }) => (
                            <Select
                              {...field}
                              isDisabled={!downloadFormWatchValue('startDate') || !downloadFormWatchValue('endDate')}
                              isMulti
                              styles={customStyles}
                              options={
                                historicSenders && Object.keys(historicSenders).length > 0
                                  ? historicSenders.items
                                    .filter((f) => f.isActive)
                                    .map((e, key) => ({
                                      value: e.idCompany,
                                      label: e.description,
                                    }))
                                  : []
                              }
                            />
                          )}
                        />
                      </Form.Group>
                    </Row>
                    <Row>
                      <Form.Group as={Col}>
                        <div>Operador</div>
                        <Controller
                          name='operator'
                          control={downloadFormControl}
                          render={({ field }) => (
                            <GroupedSelect
                              {...field}
                              groupedOptions={buildOperatorGroupedOptions(historicOperators?.items)}
                              isDisabled={!downloadFormWatchValue('startDate') || !downloadFormWatchValue('endDate') || !resolvedIdCountry}
                              isMulti
                              isClearable={true}
                            />
                          )}
                        />
                      </Form.Group>
                    </Row>
                  </Show>
                }
                <Modal.Footer>
                  <button
                    dis
                    className='btn  btn-primary float-right'
                    type='submit'>
                    Descargar <i className='fas fa-solid fa-download'></i>
                  </button>
                </Modal.Footer>
              </Form>
            </FormProvider>
          </Modal.Body>
        </Modal>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    clients: state.companyState.clients,
    logisticsOperators: state.companyState.logisticsOperators,
    historicSenders: state.companyState.historicSenders,
    historicOperators: state.companyState.historicOperators,
    statuses: state.statusesState.statuses,
    addresses: state.addressState.addresses,
    requestingFetchAddresses: state.addressState.requestingFetchAddresses,
    address: state.addressState.address,
    fileUploaded: state.addressState.fileUploaded,
    fileUploadedTrackingId: state.addressState.fileUploadedTrackingId,
    successfulCreateBulkTrackingIdAddress: state.addressState.successfulCreateBulkTrackingIdAddress,
    bulkTrackingIdOperatorAddress: state.addressState.bulkTrackingIdOperatorAddress,
    branches: state.branchState.branches,
    successfulUploadInvalidAddresses: state.addressState.successfulUploadInvalidAddresses,
    fetchInvalidAddressesData: state.addressState.fetchInvalidAddressesData,
    novelties: state.noveltyState.novelties,
    countries: state.countryState.countries,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    reduxGetStatuses: (payload) =>
      dispatch({
        type: "FETCH_NEW_STATUS_REQUEST",
        value: payload,
      }),
    reduxGetClients: (payload) =>
      dispatch({
        type: 'FETCH_COMPANIESBYSHIPPER_REQUEST',
        value: payload,
      }),
    reduxGetLogisticsOperators: (payload) =>
      dispatch({
        type: 'FETCH_COMPANIESBYLOGISTICSOPERATOR_REQUEST',
        value: payload,
      }),
    reduxGetHistoricSenders: (payload) =>
      dispatch({
        type: 'FETCH_HISTORIC_SENDERS_REQUEST',
        value: payload,
      }),
    reduxGetHistoricOperators: (payload) =>
      dispatch({
        type: 'FETCH_HISTORIC_OPERATORS_REQUEST',
        value: payload,
      }),
    reduxGetAddresses: (payload) =>
      dispatch({
        type: 'FETCH_ADDRESSES_REQUEST',
        value: payload,
      }),
    reduxResetAddressForm: () =>
      dispatch({
        type: 'RESET_ADDRESS_FORM',
      }),
    reduxUploadUpdateFile: (payload) =>
      dispatch({
        type: 'UPLOAD_UPDATE_ADDRESS_REQUEST',
        value: payload,
      }),
    reduxUpdateAddressFile: (payload) =>
      dispatch({
        type: 'UPDATE_UPLOAD_ADDRESS_REQUEST',
        value: payload,
      }),
    reduxDownloadInvalidAddresses: (payload) =>
      dispatch({
        type: 'FETCH_INVALID_ADDRESSES',
        value: payload,
      }),
    reduxResetAddressFormTrackingId: () =>
      dispatch({
        type: 'RESET_TRACKING_ID_FORM',
      }),
    reduxResetAddressesEvents: () =>
      dispatch({
        type: 'RESET_READ_BY_IDADDRESS_ADDRESSES_EVENT',
      }),
    reduxGetBranches: (payload) =>
      dispatch({
        type: 'FETCH_BRANCHES_REQUEST',
        value: payload,
      }),
    reduxGetNovelties: (payload) =>
      dispatch({
        type: "FETCH_NOVELTIES_REQUEST",
        value: payload,
      }),
    reduxGetCountries: (payload) =>
      dispatch({
        type: 'FETCH_COUNTRIES_REQUEST',
        value: payload,
      }),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Address);
